import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import api from '@/utils/api';
import { getDisplayPrice } from '@/utils/pricing';
import { telLink } from '@/utils/phone';
import { ShoppingCart, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useTenant } from '@/contexts/TenantContext';
import GripPicker from '@/components/GripPicker';
import ShaftPicker from '@/components/ShaftPicker';
import Breadcrumbs from '@/components/Breadcrumbs';
import BuildSummaryCard from '@/components/BuildSummaryCard';
import ConfigProgress from '@/components/ConfigProgress';
import SpecsTable from '@/components/SpecsTable';
import ReviewsBlock from '@/components/ReviewsBlock';
import ImageGallery from '@/components/ImageGallery';
import IronClubSelector, { DEFAULT_IRON_SET } from '@/components/IronClubSelector';

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showRequiredError, setShowRequiredError] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [purchaseMode, setPurchaseMode] = useState('custom'); // 'ready' or 'custom'
  const [offShelfOptions, setOffShelfOptions] = useState([]);
  const [selectedOffShelf, setSelectedOffShelf] = useState(null);
  const [selectedFlexFilter, setSelectedFlexFilter] = useState('');
  const [readyToShipGrip, setReadyToShipGrip] = useState('');
  const [configuration, setConfiguration] = useState({
    shaft_flex: '',
    adapter_type: '',
    tip_adapter: '',
    playing_length: '',
    tipping: '',
    grip: '',
    grip_wraps: '',
    grip_logo: '',
    dexterity: '',
    pured: '',
    notes: '',
    variants: {},  // dynamic variant groups (loft, finish, loft_bounce, weight, color, etc.)
  });
  const [availableTipAdapters, setAvailableTipAdapters] = useState([]);
  const [catalogShafts, setCatalogShafts] = useState([]);
  const [selectedShaftProduct, setSelectedShaftProduct] = useState(null);
  const [ironClubs, setIronClubs] = useState([...DEFAULT_IRON_SET]);
  const isIron = product?.category === 'irons';
  // `product.min_advertised_price` is the per-iron price; total scales linearly with club count.
  // Customer-facing price uses MAP via getDisplayPrice().
  const perIronPrice = isIron ? getDisplayPrice(product) : 0;

  // The product we source shaft-related options from:
  // - For drivers/fairways/hybrids: the customer-picked shaft product (once chosen)
  // - Otherwise: the product itself (e.g. a shaft product page)
  const shaftSource = (['drivers', 'fairways', 'hybrids'].includes(product?.category) && selectedShaftProduct)
    ? selectedShaftProduct
    : product;

  // Effective price of a catalog shaft = this product's per-product override
  // (e.g. "No Upcharge" set to $0 for FJ-style zero-upcharge shafts) if present,
  // else the shaft's own catalog MAP. Mirrors ShaftPicker.js's effectivePrice()
  // and the backend's authoritative app/routes/cart.py::calculate_item_total.
  const shaftOverrides = product?.option_price_overrides?.catalog_shaft || {};
  const getEffectiveShaftPrice = (shaft) => {
    if (!shaft) return 0;
    const ov = shaftOverrides[shaft.product_id];
    return ov === undefined || ov === null ? Number(shaft.min_advertised_price || 0) : Number(ov);
  };

  // Category -> shaft subcategory mapping
  const shaftSubcategoryFor = (cat) => ({
    drivers: 'wood', fairways: 'wood', hybrids: 'hybrid',
    irons: 'iron', wedges: 'wedge', putters: 'putter',
  })[cat];
  const needsShaftPicker = product && ['drivers', 'fairways', 'hybrids', 'irons'].includes(product.category);
  const needsDexterity = product && ['drivers', 'fairways', 'hybrids', 'irons', 'wedges'].includes(product.category);
  const DEXTERITY_OPTIONS = [
    { id: 'RH', name: 'Right Hand' },
    { id: 'LH', name: 'Left Hand' },
  ];

  useEffect(() => {
    loadProduct();
  }, [productId]);

  // Load shaft catalog for driver/fairway/hybrid products (wood/hybrid shafts)
  useEffect(() => {
    if (!needsShaftPicker) {
      setCatalogShafts([]);
      setSelectedShaftProduct(null);
      return;
    }
    const sub = shaftSubcategoryFor(product.category);
    api.get(`/products?category=shafts&subcategory=${sub}`)
      .then((res) => setCatalogShafts(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCatalogShafts([]));
  }, [product?.product_id]);
  
  // Load off-shelf options for the product category
  useEffect(() => {
    if (product && ['drivers', 'fairways', 'hybrids', 'irons', 'wedges', 'putters'].includes(product.category)) {
      loadOffShelfOptions(product.category);
    }
  }, [product]);
  
  const loadOffShelfOptions = async (category) => {
    try {
      const response = await api.get(`/off-shelf-configs/${category}`);
      setOffShelfOptions(response.data);
      // If off-shelf options exist, default to ready-to-ship mode
      if (response.data.length > 0) {
        setPurchaseMode('ready');
      }
    } catch (error) {
      console.error('Failed to load off-shelf options');
    }
  };
  
  // Check for and apply fitting recommendation
  const applyFittingRecommendation = (productData) => {
    const saved = localStorage.getItem('fittingRecommendation');
    if (saved && productData.category === 'shafts') {
      try {
        const rec = JSON.parse(saved);
        
        // Apply flex recommendation if we have shaft flex options
        if (rec.flex_code && productData.shaft_flex_options?.length > 0) {
          // Try to find a matching flex option
          // Priority: exact code match (6S, 5S) > contains code (S) > contains name (Stiff)
          let matchingFlex = null;
          
          // First try: Find options ending with the flex code (e.g., "6S" for Stiff)
          matchingFlex = productData.shaft_flex_options.find(opt => {
            const optId = opt.id.toUpperCase();
            const optName = opt.name.toUpperCase();
            // Match options like "6S", "5S" for Stiff (S)
            return optId.endsWith(rec.flex_code) || optName.endsWith(rec.flex_code);
          });
          
          // Second try: Find options containing the flex code
          if (!matchingFlex) {
            matchingFlex = productData.shaft_flex_options.find(opt => {
              const optId = opt.id.toUpperCase();
              return optId.includes(rec.flex_code);
            });
          }
          
          // Third try: Match by flex name (Stiff, Regular, etc.)
          if (!matchingFlex && rec.flex) {
            matchingFlex = productData.shaft_flex_options.find(opt => {
              const optName = opt.name.toUpperCase();
              return optName.includes(rec.flex);
            });
          }
          
          if (matchingFlex) {
            setConfiguration(prev => ({ ...prev, shaft_flex: matchingFlex.id }));
            toast.success(`Applied recommended flex: ${matchingFlex.name}`);
          } else {
            toast.info(`Recommended flex: ${rec.flex || rec.flex_code} - Please select a matching option`);
          }
        }
        
        // Switch to custom build mode
        setPurchaseMode('custom');
        
      } catch (e) {
        console.error('Failed to apply fitting recommendation:', e);
      }
    }
  };
  
  useEffect(() => {
    // Update available tip adapters based on selected adapter type — source from shaftSource
    if (configuration.adapter_type && shaftSource?.adapter_type_options) {
      const adapterType = shaftSource.adapter_type_options.find(
        at => at.id === configuration.adapter_type
      );
      if (adapterType && adapterType.tip_adapters) {
        setAvailableTipAdapters(adapterType.tip_adapters);
        // For driver/fairway/hybrid products, the tip-adapter UI is hidden because
        // the product's brand already determines the correct adapter. Auto-select
        // the first available tip adapter so the cart config always carries one.
        const autoPick = ['drivers', 'fairways', 'hybrids'].includes(product?.category);
        setConfiguration(prev => ({
          ...prev,
          tip_adapter: autoPick && adapterType.tip_adapters.length > 0
            ? adapterType.tip_adapters[0].id
            : '',
        }));
      }
    }
  }, [configuration.adapter_type, shaftSource?.product_id, product?.category]);
  
  const loadProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/products/${productId}`);
      // Apply per-product price overrides to option lists so every downstream
      // read (pickers, cart submit, build breakdown) sees the effective modifier.
      const raw = response.data;
      const overrides = raw?.option_price_overrides || {};
      const CATEGORY_TO_FIELD = {
        shaft_flex: 'shaft_flex_options',
        grip: 'grip_options',
        grip_wraps: 'grip_wraps_options',
        grip_logo: 'grip_logo_options',
        playing_length: 'playing_length_options',
        tipping: 'tipping_options',
        pured: 'pured_options',
        adapter_type: 'adapter_type_options',
      };
      const patched = { ...raw };
      for (const [cat, field] of Object.entries(CATEGORY_TO_FIELD)) {
        const catOverrides = overrides[cat];
        if (!catOverrides || !Array.isArray(patched[field])) continue;
        patched[field] = patched[field].map((opt) =>
          Object.prototype.hasOwnProperty.call(catOverrides, opt.id)
            ? { ...opt, price_modifier: Number(catOverrides[opt.id]), _override_applied: true }
            : opt
        );
      }
      setProduct(patched);
      
      // Auto-set club type based on product category
      const categoryToClubType = {
        'drivers': 'driver',
        'fairways': 'fairway',
        'hybrids': 'hybrid',
        'irons': 'iron',
        'wedges': 'wedge',
        'putters': 'putter'
      };
      const autoClubType = categoryToClubType[patched.category];
      if (autoClubType && patched.adapter_type_options?.length > 0) {
        const match = patched.adapter_type_options.find(opt => 
          opt.id.toLowerCase().includes(autoClubType) || opt.name.toLowerCase().includes(autoClubType)
        );
        if (match) {
          setConfiguration(prev => ({ ...prev, adapter_type: match.id }));
        }
      }
      
      // Apply any fitting recommendations after product loads
      applyFittingRecommendation(patched);
    } catch (error) {
      toast.error('Failed to load product');
      navigate(tenantPath('/category/shafts'));
    } finally {
      setLoading(false);
    }
  };
  
  // Map option keys to configuration keys
  const optionKeyToConfigKey = {
    'shaft_flex': 'shaft_flex',
    'adapter_type': 'adapter_type',
    'playing_length': 'playing_length',
    'tipping': 'tipping',
    'grip': 'grip',
    'grip_wraps': 'grip_wraps',
    'grip_logo': 'grip_logo',
    'pured': 'pured'
  };

  // Map option keys to display names
  const optionKeyToDisplayName = {
    'shaft_flex': 'Shaft Flex',
    'adapter_type': 'Club Type',
    'playing_length': 'Playing Length',
    'tipping': 'Tipping',
    'grip': 'Grip',
    'grip_wraps': 'Grip Wraps',
    'grip_logo': 'Grip Logo',
    'pured': 'Pured'
  };

  // Which options array backs each option key, and whether it's sourced
  // from the shaft (shaftSource) or the product itself. Mirrors the exact
  // conditions used to render each Select below - an option key can only
  // ever be "required" if the customer actually has a UI control to set it.
  const optionKeyToOptionsList = {
    'shaft_flex': () => shaftSource?.shaft_flex_options,
    'adapter_type': () => shaftSource?.adapter_type_options,
    'playing_length': () => shaftSource?.playing_length_options,
    'tipping': () => shaftSource?.tipping_options,
    'pured': () => shaftSource?.pured_options,
    'grip': () => product?.grip_options,
    'grip_wraps': () => product?.grip_wraps_options,
    'grip_logo': () => product?.grip_logo_options,
  };

  const validateRequiredOptions = () => {
    if (!product?.required_options || product.required_options.length === 0) {
      return { valid: true, missing: [] };
    }

    const missing = [];
    for (const optionKey of product.required_options) {
      const configKey = optionKeyToConfigKey[optionKey];
      const optionsList = optionKeyToOptionsList[optionKey]?.();
      // Data-integrity guard: never block Add to Cart on a required option
      // that has zero selectable choices - there'd be no way to satisfy it.
      if (configKey && Array.isArray(optionsList) && optionsList.length > 0 && !configuration[configKey]) {
        missing.push(optionKeyToDisplayName[optionKey] || optionKey);
      }
    }

    return { valid: missing.length === 0, missing };
  };

  const handleAddToCart = async () => {
    if (product?.is_call_for_pricing) {
      toast.error('This item is not available for online purchase right now - call for pricing.');
      return;
    }
    // For Ready to Ship mode, validate off-shelf selection and grip
    if (purchaseMode === 'ready') {
      if (!selectedFlexFilter) {
        toast.error('Please select a shaft flex');
        return;
      }
      if (!selectedOffShelf) {
        toast.error('Please select a shaft');
        return;
      }
      if (product.grip_options?.length > 0 && !readyToShipGrip) {
        toast.error('Please select a grip');
        return;
      }
      
      const offShelfConfig = offShelfOptions.find(o => o.config_id === selectedOffShelf);
      if (!offShelfConfig) {
        toast.error('Invalid selection');
        return;
      }
      
      // Get grip name for display
      const selectedGripOption = product.grip_options?.find(g => g.id === readyToShipGrip);
      
      try {
        setAdding(true);
        
        const cartConfig = {
          purchase_mode: 'ready_to_ship',
          off_shelf_config_id: offShelfConfig.config_id,
          shaft_name: offShelfConfig.shaft_name,
          shaft_flex: offShelfConfig.shaft_flex,
          shaft_weight: offShelfConfig.shaft_weight,
          grip: readyToShipGrip,
          grip_name: selectedGripOption?.name || ''
        };
        
        // Try server cart first
        try {
          await api.post('/cart', {
            product_id: productId,
            quantity: 1,
            configuration: cartConfig,
            price_override: offShelfConfig.price
          });
          toast.success('Added to cart!');
          navigate(tenantPath('/cart'));
          return;
        } catch (error) {
          if (error.response?.status === 401) {
            // Guest cart
            const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
            const cartItem = {
              cart_item_id: `guest_${Date.now()}`,
              product_id: productId,
              product_name: `${product.name} - ${offShelfConfig.name}`,
              product_image: offShelfConfig.image_url || product.image_url,
              base_price: offShelfConfig.price,
              quantity: 1,
              configuration: cartConfig,
              total_price: offShelfConfig.price
            };
            guestCart.push(cartItem);
            localStorage.setItem('guestCart', JSON.stringify(guestCart));
            toast.success('Added to cart!');
            navigate(tenantPath('/cart'));
            return;
          }
          throw error;
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to add to cart');
      } finally {
        setAdding(false);
      }
      return;
    }
    
    // Custom build mode - aggregate ALL missing required fields in one pass
    const allMissing = [];

    // Dexterity first (primary selector at the top of the PDP)
    if (needsDexterity && !configuration.dexterity) {
      allMissing.push('Dexterity');
    }

    // Dynamic variant groups (Loft, Finish, Loft/Bounce, Weight, Color, Head Type, ...)
    (product.variant_options || [])
      .filter((g) => g.required && !configuration.variants?.[g.key])
      .forEach((g) => allMissing.push(g.name));

    // Irons: at least one club number must be selected
    if (isIron && ironClubs.length === 0) {
      allMissing.push('Club Numbers');
    }

    // Drivers/fairways/hybrids: a shaft must be selected from the catalog
    if (needsShaftPicker && !selectedShaftProduct) {
      allMissing.push('Shaft');
    }

    // Product-level required_options (shaft_flex, grip, adapter_type, playing_length, etc.)
    const validation = validateRequiredOptions();
    if (!validation.valid) {
      validation.missing.forEach((name) => {
        if (!allMissing.includes(name)) allMissing.push(name);
      });
    }

    if (allMissing.length > 0) {
      setShowRequiredError(true);
      setMissingFields(allMissing);
      toast.error(`Please select: ${allMissing.join(', ')}`);
      return;
    }

    // Clear error state if validation passes
    setShowRequiredError(false);
    setMissingFields([]);

    try {
      setAdding(true);
      
      // Resolve option ids -> display names so the cart can show human-readable values
      const optName = (opts, id) => (opts || []).find((o) => o.id === id)?.name || id || '';
      const gripOpt = (product.grip_options || []).find((o) => o.id === configuration.grip);

      // Compute the same authoritative line total the render path shows, so
      // the guest-cart fallback below stores the right number. (Server cart
      // recomputes server-side; this path only matters for guests, but the
      // total must include shaft + every option upcharge — not just base.)
      const lookupOpt = (opts, id) => (opts || []).find((o) => o.id === id);
      let computedExtras = 0;
      if (selectedShaftProduct) {
        computedExtras += getEffectiveShaftPrice(selectedShaftProduct);
      }
      const _shaftOptPairs = [
        [shaftSource?.shaft_flex_options, configuration.shaft_flex],
        [shaftSource?.playing_length_options, configuration.playing_length],
        [shaftSource?.tipping_options, configuration.tipping],
        [shaftSource?.pured_options, configuration.pured],
      ];
      for (const [list, id] of _shaftOptPairs) {
        const o = lookupOpt(list, id);
        if (o) computedExtras += Number(o.price_modifier || 0);
      }
      const _parentOptPairs = [
        [product.grip_options, configuration.grip],
        [product.grip_wraps_options, configuration.grip_wraps],
        [product.grip_logo_options, configuration.grip_logo],
        [product.adapter_type_options, configuration.adapter_type],
      ];
      for (const [list, id] of _parentOptPairs) {
        const o = lookupOpt(list, id);
        if (o) computedExtras += Number(o.price_modifier || 0);
      }
      for (const g of (product.variant_options || [])) {
        const selId = configuration.variants?.[g.key];
        if (!selId) continue;
        const o = (g.options || []).find((x) => x.id === selId);
        if (o) computedExtras += Number(o.price_modifier || 0);
      }
      const computedBase = isIron
        ? Number(perIronPrice || 0) * ironClubs.length
        : getDisplayPrice(product);
      const computedTotal = Number((computedBase + computedExtras).toFixed(2));

      const customConfig = {
        ...configuration,
        purchase_mode: 'custom_build',
        // Human-readable labels
        shaft_flex_name: optName(shaftSource?.shaft_flex_options, configuration.shaft_flex),
        adapter_type_name: optName(shaftSource?.adapter_type_options, configuration.adapter_type),
        playing_length_name: optName(shaftSource?.playing_length_options, configuration.playing_length),
        tipping_name: optName(shaftSource?.tipping_options, configuration.tipping),
        pured_name: optName(shaftSource?.pured_options, configuration.pured),
        grip_name: gripOpt?.name || '',
        grip_image: gripOpt?.image_url || '',
        grip_wraps_name: optName(product.grip_wraps_options, configuration.grip_wraps),
        grip_logo_name: optName(product.grip_logo_options, configuration.grip_logo),
        dexterity_name: (DEXTERITY_OPTIONS.find(o => o.id === configuration.dexterity) || {}).name || '',
        // Dynamic variant selections with display-ready names and modifiers
        variant_selections: (product.variant_options || []).reduce((acc, g) => {
          const selectedId = configuration.variants?.[g.key];
          if (!selectedId) return acc;
          const opt = (g.options || []).find((o) => o.id === selectedId);
          if (opt) {
            acc[g.key] = {
              group_name: g.name,
              option_id: opt.id,
              option_name: opt.name,
              price_modifier: Number(opt.price_modifier || 0),
            };
          }
          return acc;
        }, {}),
        // Iron set composition (only meaningful when category === 'irons')
        ...(isIron ? {
          iron_clubs: ironClubs,
          iron_club_count: ironClubs.length,
          iron_per_price: perIronPrice,
          iron_total_base: perIronPrice * ironClubs.length,
        } : {}),
        // When customer picked a shaft from the catalog (drivers/fairways/hybrids),
        // persist which shaft they chose plus its price so the cart can surface it.
        ...(selectedShaftProduct ? {
          shaft_product_id: selectedShaftProduct.product_id,
          shaft_name: selectedShaftProduct.name,
          shaft_manufacturer: selectedShaftProduct.manufacturer,
          shaft_image: selectedShaftProduct.image_url,
          shaft_price: getEffectiveShaftPrice(selectedShaftProduct),
        } : {}),
      };
      
      // Try to add to server cart first (for logged-in users)
      try {
        await api.post('/cart', {
          product_id: productId,
          quantity: 1,
          configuration: customConfig
        });
        toast.success('Added to cart!');
        navigate(tenantPath('/cart'));
        return;
      } catch (error) {
        // If 401 (not authenticated), use guest cart
        if (error.response?.status === 401) {
          // Add to guest cart in localStorage
          const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
          
          const cartItem = {
            cart_item_id: `guest_${Date.now()}`,
            product_id: productId,
            product_name: product.name,
            product_image: product.image_url,
            base_price: getDisplayPrice(product),
            quantity: 1,
            configuration: customConfig,
            total_price: computedTotal
          };
          
          guestCart.push(cartItem);
          localStorage.setItem('guestCart', JSON.stringify(guestCart));
          
          toast.success('Added to cart!');
          navigate(tenantPath('/cart'));
          return;
        }
        throw error;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setAdding(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-6 py-24 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }
  
  if (!product) return null;

  // ---- Live price calculation ----
  const lookup = (opts, id) => (opts || []).find((o) => o.id === id);
  const lineItems = [];
  let runningExtras = 0;

  // Dexterity line
  if (configuration.dexterity) {
    const d = DEXTERITY_OPTIONS.find((o) => o.id === configuration.dexterity);
    if (d) lineItems.push({ label: 'Dexterity', sub: d.name, amount: 0 });
  }
  // Shaft line (from catalog picker, for drivers/fairways/hybrids)
  if (selectedShaftProduct) {
    const p = getEffectiveShaftPrice(selectedShaftProduct);
    runningExtras += p;
    lineItems.push({
      label: 'Shaft',
      sub: `${selectedShaftProduct.manufacturer} · ${selectedShaftProduct.name}`.slice(0, 60),
      amount: p,
    });
  }
  // Options from shaftSource
  const shaftOptPairs = [
    ['Flex', 'shaft_flex_options', configuration.shaft_flex],
    ['Length', 'playing_length_options', configuration.playing_length],
    ['Tipping', 'tipping_options', configuration.tipping],
    ['Pured', 'pured_options', configuration.pured],
  ];
  for (const [label, key, id] of shaftOptPairs) {
    const opt = lookup(shaftSource?.[key], id);
    if (opt) {
      const m = Number(opt.price_modifier || 0);
      runningExtras += m;
      lineItems.push({ label, sub: opt.name, amount: m });
    }
  }
  // Grip / grip_wraps / grip_logo from parent product
  const parentOptPairs = [
    ['Grip', 'grip_options', configuration.grip],
    ['Grip Wraps', 'grip_wraps_options', configuration.grip_wraps],
    ['Logo', 'grip_logo_options', configuration.grip_logo],
  ];
  for (const [label, key, id] of parentOptPairs) {
    const opt = lookup(product?.[key], id);
    if (opt) {
      const m = Number(opt.price_modifier || 0);
      runningExtras += m;
      lineItems.push({ label, sub: opt.name, amount: m });
    }
  }

  const basePrice = getDisplayPrice(product);
  // For irons, the base "head" price scales by the number of clubs in the set.
  // For everything else, use the full product price as the base.
  const adjustedBase = isIron ? perIronPrice * ironClubs.length : basePrice;

  // Dynamic variant groups (Loft, Finish, Loft/Bounce, Weight, Color, ...)
  for (const g of (product.variant_options || [])) {
    const selId = configuration.variants?.[g.key];
    if (!selId) continue;
    const opt = (g.options || []).find((o) => o.id === selId);
    if (!opt) continue;
    const m = Number(opt.price_modifier || 0);
    runningExtras += m;
    lineItems.push({ label: g.name, sub: opt.name, amount: m });
  }

  const liveTotal = adjustedBase + runningExtras;

  // ---- Step progress ----
  const progressSteps = needsShaftPicker
    ? [
        { label: 'Shaft', done: !!selectedShaftProduct },
        { label: 'Flex', done: !!configuration.shaft_flex },
        { label: 'Length', done: !!configuration.playing_length },
        { label: 'Grip', done: !!configuration.grip },
        { label: 'Review', done: false },
      ]
    : [
        { label: 'Flex', done: !!configuration.shaft_flex },
        { label: 'Adapter', done: !!configuration.adapter_type },
        { label: 'Length', done: !!configuration.playing_length },
        { label: 'Grip', done: !!configuration.grip },
        { label: 'Review', done: false },
      ];

  // ---- Breadcrumbs ----
  const categoryLabel = (product.category || '').charAt(0).toUpperCase() + (product.category || '').slice(1);
  const breadcrumbs = [
    { label: categoryLabel, to: tenantPath(`/category/${product.category}`) },
    { label: product.name },
  ];

  // ---- Specs (no MAP shown to customers) ----
  const sc = product.shaft_characteristics || {};
  const specs = product.category === 'shafts'
    ? {
        'Manufacturer': product.manufacturer,
        'Material': sc.material,
        'Launch': sc.launch,
        'Spin': sc.spin,
        'Torque': sc.torque,
        'Kick Point': sc.kick_point,
        'Bend Profile': sc.bend_profile,
        'Balance Point': sc.balance_point,
      }
    : {
        'Manufacturer': product.manufacturer,
        'Category': categoryLabel,
        'Subcategory': product.subcategory,
      };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
        <Breadcrumbs items={breadcrumbs} />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left: Image gallery (sticky on lg) */}
        <div className="lg:col-span-4 lg:sticky lg:top-20 lg:self-start">
          <ImageGallery images={[product.image_url]} alt={product.name} />
        </div>

        {/* Middle: Configurator */}
        <div className="lg:col-span-5 bg-white" data-testid="product-configurator">
            {/* Product Info */}
            <div className="mb-6">
              <p className="text-xs text-[#334155] font-bold uppercase tracking-widest mb-2">{product.manufacturer}</p>
              <h1
                className="text-3xl md:text-4xl font-bold text-gray-900 mb-3"
                style={{ fontFamily: 'Chivo, sans-serif' }}
                data-testid="product-name"
              >
                {product.name}
              </h1>
              <p className="text-2xl font-bold text-[#334155] mb-4" data-testid="product-price">
                {product.is_call_for_pricing ? (
                  'Call for Pricing'
                ) : purchaseMode === 'ready' && selectedOffShelf ? (
                  `$${offShelfOptions.find(o => o.config_id === selectedOffShelf)?.price.toFixed(2) || getDisplayPrice(product).toFixed(2)}`
                ) : isIron ? (
                  <>
                    ${getDisplayPrice(product).toFixed(2)}
                    <span className="text-sm font-medium text-gray-500 ml-2">per iron</span>
                  </>
                ) : (
                  `$${getDisplayPrice(product).toFixed(2)}`
                )}
              </p>
              <p className="text-gray-600 leading-relaxed text-sm">{product.description}</p>

              {product.is_call_for_pricing && (
                <div className="mt-4 inline-flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 px-4 py-2 rounded-lg text-sm font-bold" data-testid="call-for-pricing-notice">
                  <span>Call for Pricing — not available for online purchase right now</span>
                  {product.call_for_pricing_phone && (
                    <>
                      <span className="text-amber-400">·</span>
                      <a href={telLink(product.call_for_pricing_phone)} className="underline hover:text-amber-900" data-testid="call-for-pricing-phone-link">
                        {product.call_for_pricing_phone}
                      </a>
                    </>
                  )}
                </div>
              )}

              {product.stock === 0 && (
                <div className="mt-4 inline-block bg-[#78716C]/10 border border-[#78716C] text-gray-500 px-4 py-2 rounded-lg text-sm font-bold">
                  Out of Stock
                </div>
              )}

              {/* Step progress */}
              {(purchaseMode === 'custom' || offShelfOptions.length === 0) && (
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <ConfigProgress steps={progressSteps} />
                </div>
              )}

              {/* Shaft Characteristics - Only for shaft products */}
              {product.category === 'shafts' && product.shaft_characteristics && (
                <div className="mt-6 p-4 bg-[#F5F3EF] rounded-lg border border-[#E8E4DC]">
                  <h3 className="text-sm font-bold text-[#2C3E2D] uppercase tracking-wide mb-3">Shaft Specifications</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {product.shaft_characteristics.material && (
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-xs text-[#5A6B5C] uppercase">Material</p>
                        <p className="text-sm font-semibold text-[#2C3E2D]">{product.shaft_characteristics.material}</p>
                      </div>
                    )}
                    {product.shaft_characteristics.torque && (
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-xs text-[#5A6B5C] uppercase">Torque</p>
                        <p className="text-sm font-semibold text-[#2C3E2D]">{product.shaft_characteristics.torque}</p>
                      </div>
                    )}
                    {product.shaft_characteristics.kick_point && (
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-xs text-[#5A6B5C] uppercase">Kick Point</p>
                        <p className="text-sm font-semibold text-[#2C3E2D]">{product.shaft_characteristics.kick_point}</p>
                      </div>
                    )}
                    {product.shaft_characteristics.bend_profile && (
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-xs text-[#5A6B5C] uppercase">Bend Profile</p>
                        <p className="text-sm font-semibold text-[#2C3E2D]">{product.shaft_characteristics.bend_profile}</p>
                      </div>
                    )}
                    {product.shaft_characteristics.launch && (
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-xs text-[#5A6B5C] uppercase">Launch</p>
                        <p className="text-sm font-semibold text-[#2C3E2D]">{product.shaft_characteristics.launch}</p>
                      </div>
                    )}
                    {product.shaft_characteristics.spin && (
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-xs text-[#5A6B5C] uppercase">Spin</p>
                        <p className="text-sm font-semibold text-[#2C3E2D]">{product.shaft_characteristics.spin}</p>
                      </div>
                    )}
                    {product.shaft_characteristics.balance_point && (
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-xs text-[#5A6B5C] uppercase">Balance Pt</p>
                        <p className="text-sm font-semibold text-[#2C3E2D]">{product.shaft_characteristics.balance_point}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Purchase Mode Toggle - Only show if off-shelf options exist */}
            {offShelfOptions.length > 0 && (
              <div className="mb-8">
                <div className="flex rounded-lg bg-gray-100 p-1">
                  <button
                    onClick={() => setPurchaseMode('ready')}
                    className={`flex-1 py-3 px-4 rounded-md font-bold text-sm uppercase tracking-wider transition-all ${
                      purchaseMode === 'ready' 
                        ? 'bg-[#4A6741] text-white shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    data-testid="ready-to-ship-tab"
                  >
                    Ready to Ship
                  </button>
                  <button
                    onClick={() => setPurchaseMode('custom')}
                    className={`flex-1 py-3 px-4 rounded-md font-bold text-sm uppercase tracking-wider transition-all ${
                      purchaseMode === 'custom' 
                        ? 'bg-[#4A6741] text-white shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    data-testid="custom-build-tab"
                  >
                    Custom Build
                  </button>
                </div>
              </div>
            )}
            
            {/* Ready to Ship Options */}
            {purchaseMode === 'ready' && offShelfOptions.length > 0 && (
              <div className="space-y-6">
                {/* Step 1: Select Flex */}
                <div>
                  <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-2 block">
                    1. Select Shaft Flex
                  </Label>
                  <Select value={selectedFlexFilter} onValueChange={(value) => { setSelectedFlexFilter(value); setSelectedOffShelf(null); }}>
                    <SelectTrigger className="w-full bg-gray-50 text-gray-900 border-[#F3F4F6]" data-testid="ready-flex-select">
                      <SelectValue placeholder="Choose your flex" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg">
                      {/* Get unique flex values from off-shelf options */}
                      {[...new Set(offShelfOptions.map(o => o.shaft_flex))].map(flex => (
                        <SelectItem key={flex} value={flex} className="text-gray-900 hover:bg-gray-100">
                          {flex}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Step 2: Select Shaft Configuration - Only show after flex is selected */}
                {selectedFlexFilter && (
                  <div>
                    <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-3 block">
                      2. Select Shaft
                    </Label>
                    <div className="space-y-3">
                      {offShelfOptions
                        .filter(option => option.shaft_flex === selectedFlexFilter)
                        .map(option => (
                        <div
                          key={option.config_id}
                          onClick={() => setSelectedOffShelf(option.config_id)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedOffShelf === option.config_id
                              ? 'border-[#4A6741] bg-[#4A6741]/5'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}
                          data-testid={`off-shelf-option-${option.config_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                {selectedOffShelf === option.config_id && (
                                  <div className="w-6 h-6 rounded-full bg-[#4A6741] flex items-center justify-center flex-shrink-0">
                                    <Check className="h-4 w-4 text-white" />
                                  </div>
                                )}
                                <div>
                                  <h4 className="font-bold text-gray-900">{option.shaft_name}</h4>
                                  <p className="text-sm text-gray-600">
                                    {option.shaft_flex}
                                    {option.shaft_weight && ` • ${option.shaft_weight}`}
                                  </p>
                                  {option.description && (
                                    <p className="text-xs text-gray-500 mt-1">{option.description}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-[#334155]">${option.price.toFixed(2)}</p>
                              {option.stock <= 5 && option.stock > 0 && (
                                <p className="text-xs text-[#D4A55A]">Only {option.stock} left</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {offShelfOptions.filter(option => option.shaft_flex === selectedFlexFilter).length === 0 && (
                        <p className="text-gray-500 text-center py-4">No shafts available in this flex</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Step 3: Select Grip - Show after shaft is selected */}
                {selectedOffShelf && product.grip_options?.length > 0 && (
                  <div>
                    <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-2 block">
                      3. Select Grip
                    </Label>
                    <Select value={readyToShipGrip} onValueChange={setReadyToShipGrip}>
                      <SelectTrigger className="w-full bg-gray-50 text-gray-900 border-[#F3F4F6]" data-testid="ready-grip-select">
                        <SelectValue placeholder="Choose your grip" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg">
                        {product.grip_options.map(option => (
                          <SelectItem key={option.id} value={option.id} className="text-gray-900 hover:bg-gray-100">
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            
            {/* Custom Build Configuration - Only show in custom mode or if no off-shelf options */}
            {(purchaseMode === 'custom' || offShelfOptions.length === 0) && (
              <div className="space-y-6">
                {/* Dexterity — applicable to drivers/fairways/hybrids/irons/wedges */}
                {needsDexterity && (
                  <div data-testid="dexterity-section">
                    <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-2 block">
                      Dexterity <span className="text-[#D4A55A] ml-1">*</span>
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {DEXTERITY_OPTIONS.map((opt) => {
                        const on = configuration.dexterity === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setConfiguration((prev) => ({ ...prev, dexterity: opt.id }))}
                            className={`h-12 rounded-lg border-2 text-sm font-semibold transition-all ${
                              on
                                ? 'bg-[#334155] text-white border-[#334155]'
                                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                            }`}
                            data-testid={`dexterity-${opt.id}`}
                          >
                            {opt.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Dynamic variant groups (Loft, Finish, Loft/Bounce, Weight, Color, ...) */}
                {(product.variant_options || []).map((g) => {
                  const selected = configuration.variants?.[g.key] || '';
                  const missing = g.required && !selected;
                  const isPillGroup = (g.options || []).length <= 6;
                  return (
                    <div key={g.key} data-testid={`variant-group-${g.key}`}>
                      <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-2 block">
                        {g.name}
                        {g.required && <span className="text-[#D4A55A] ml-1">*</span>}
                      </Label>
                      {isPillGroup ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {(g.options || []).map((opt) => {
                            const on = selected === opt.id;
                            const pm = Number(opt.price_modifier || 0);
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                onClick={() =>
                                  setConfiguration((prev) => ({
                                    ...prev,
                                    variants: { ...(prev.variants || {}), [g.key]: opt.id },
                                  }))
                                }
                                className={`h-12 px-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                                  on
                                    ? 'bg-[#334155] text-white border-[#334155]'
                                    : missing
                                    ? 'bg-white text-gray-700 border-[#D4A55A]'
                                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                                }`}
                                data-testid={`variant-${g.key}-${opt.id}`}
                              >
                                <span>{opt.name}</span>
                                {pm > 0 && (
                                  <span className={`ml-1 text-xs ${on ? 'text-white/80' : 'text-gray-500'}`}>
                                    (+${pm.toFixed(2)})
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <Select
                          value={selected}
                          onValueChange={(value) =>
                            setConfiguration((prev) => ({
                              ...prev,
                              variants: { ...(prev.variants || {}), [g.key]: value },
                            }))
                          }
                        >
                          <SelectTrigger
                            className={`w-full bg-gray-50 text-gray-900 ${
                              missing ? 'border-[#D4A55A]' : 'border-[#F3F4F6]'
                            }`}
                            data-testid={`variant-${g.key}-select`}
                          >
                            <SelectValue placeholder={`Choose ${g.name.toLowerCase()}`} />
                          </SelectTrigger>
                          <SelectContent className="bg-white border border-gray-200 shadow-lg max-h-72">
                            {(g.options || []).map((opt) => {
                              const pm = Number(opt.price_modifier || 0);
                              return (
                                <SelectItem
                                  key={opt.id}
                                  value={opt.id}
                                  className="text-gray-900 hover:bg-gray-100"
                                >
                                  {opt.name}
                                  {pm > 0 && (
                                    <span className="ml-2 text-xs text-gray-500">
                                      (+${pm.toFixed(2)})
                                    </span>
                                  )}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  );
                })}

                {/* Iron club composition — only for iron products */}
                {isIron && (
                  <IronClubSelector
                    value={ironClubs}
                    onChange={setIronClubs}
                    perIronPrice={perIronPrice}
                  />
                )}

                {/* Shaft Picker — full catalog for drivers/fairways/hybrids (same UX as shaft product page) */}
                {needsShaftPicker && catalogShafts.length > 0 && (
                  <ShaftPicker
                    shafts={catalogShafts}
                    catalogShaftOverrides={product?.option_price_overrides?.catalog_shaft || {}}
                    value={selectedShaftProduct?.product_id || ''}
                    onChange={(pid) => {
                      const s = catalogShafts.find(x => x.product_id === pid);
                      setSelectedShaftProduct(s || null);
                      // Auto-select the adapter_type that matches the parent product's category
                      const clubType = { drivers: 'driver', fairways: 'fairway', hybrids: 'hybrid' }[product.category];
                      const matchedAdapter = s?.adapter_type_options?.find(
                        a => a.id === clubType || a.id.toLowerCase().includes(clubType) || a.name.toLowerCase().includes(clubType)
                      );
                      // Reset shaft-dependent selections when changing shafts
                      setConfiguration(prev => ({
                        ...prev,
                        shaft_flex: '',
                        adapter_type: matchedAdapter?.id || '',
                        tip_adapter: '',
                        playing_length: '',
                        tipping: '',
                        pured: '',
                      }));
                      setAvailableTipAdapters(matchedAdapter?.tip_adapters || []);
                    }}
                    required
                    testId="shaft-picker"
                  />
                )}

                {/* Driver/fairway/hybrid users must pick a shaft before seeing cascading options */}
                {needsShaftPicker && !selectedShaftProduct && (
                  <p className="text-sm text-gray-500 italic">Select a shaft above to configure flex, adapter, length, and more.</p>
                )}

                {/* Shaft Flex (from selected shaft when shaft picker is enabled) */}
                {(!needsShaftPicker || selectedShaftProduct) && shaftSource?.shaft_flex_options?.length > 0 && (
                  <div>
                    <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-2 block">
                      Shaft Flex
                      {shaftSource.required_options?.includes('shaft_flex') && <span className="text-[#D4A55A] ml-1">*</span>}
                    </Label>
                    <Select value={configuration.shaft_flex} onValueChange={(value) => setConfiguration(prev => ({ ...prev, shaft_flex: value }))}>
                      <SelectTrigger className={`w-full bg-gray-50 text-gray-900 ${shaftSource.required_options?.includes('shaft_flex') && !configuration.shaft_flex ? 'border-[#D4A55A]' : 'border-[#F3F4F6]'}`} data-testid="shaft-flex-select">
                        <SelectValue placeholder="Choose flex" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg">
                        {shaftSource.shaft_flex_options.map(option => (
                          <SelectItem key={option.id} value={option.id} className="text-gray-900 hover:bg-gray-100">
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Adapter Type (Club Type) — hidden for drivers/fairways/hybrids because
                    the club's brand already implies the adapter. Kept for other categories
                    (irons/wedges/putters/shafts) where the customer legitimately picks the head-family. */}
                {(!needsShaftPicker || selectedShaftProduct)
                  && shaftSource?.adapter_type_options?.length > 0
                  && !['drivers', 'fairways', 'hybrids'].includes(product.category) && (
                  <div>
                    <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-2 block">
                      Club Type
                      {shaftSource.required_options?.includes('adapter_type') && <span className="text-[#D4A55A] ml-1">*</span>}
                    </Label>
                    {['irons','wedges','putters'].includes(product.category) ? (
                      <div className="w-full bg-gray-100 border border-[#F3F4F6] rounded-md px-3 py-2 text-gray-700 text-sm" data-testid="adapter-type-locked">
                        {shaftSource.adapter_type_options.find(o => o.id === configuration.adapter_type)?.name || product.category.replace(/s$/, '').replace(/^./, c => c.toUpperCase())}
                      </div>
                    ) : (
                    <Select value={configuration.adapter_type} onValueChange={(value) => setConfiguration(prev => ({ ...prev, adapter_type: value }))}>
                      <SelectTrigger className={`w-full bg-gray-50 text-gray-900 ${shaftSource.required_options?.includes('adapter_type') && !configuration.adapter_type ? 'border-[#D4A55A]' : 'border-[#F3F4F6]'}`} data-testid="adapter-type-select">
                        <SelectValue placeholder="Choose club type" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg">
                        {shaftSource.adapter_type_options.map(option => (
                          <SelectItem key={option.id} value={option.id} className="text-gray-900 hover:bg-gray-100">
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    )}
                  </div>
                )}

                {/* Tip Adapter — hidden for drivers/fairways/hybrids because the product's
                    brand already determines the tip adapter. Auto-selected on shaft pick. */}
                {(!needsShaftPicker || selectedShaftProduct)
                  && availableTipAdapters.length > 0
                  && !['drivers', 'fairways', 'hybrids'].includes(product.category) && (
                  <div>
                    <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-2 block">Tip Adapter</Label>
                    <Select value={configuration.tip_adapter} onValueChange={(value) => setConfiguration(prev => ({ ...prev, tip_adapter: value }))}>
                      <SelectTrigger className="w-full bg-gray-50 border-[#F3F4F6] text-gray-900" data-testid="tip-adapter-select">
                        <SelectValue placeholder="Choose adapter" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg">
                        {availableTipAdapters.map(adapter => (
                          <SelectItem key={adapter.id} value={adapter.id} className="text-gray-900 hover:bg-gray-100">
                            {adapter.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Playing Length */}
                {(!needsShaftPicker || selectedShaftProduct) && shaftSource?.playing_length_options?.length > 0 && (
                  <div>
                    <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-2 block">
                      Playing Length
                      {shaftSource.required_options?.includes('playing_length') && <span className="text-[#D4A55A] ml-1">*</span>}
                    </Label>
                    <Select value={configuration.playing_length} onValueChange={(value) => setConfiguration(prev => ({ ...prev, playing_length: value }))}>
                      <SelectTrigger className={`w-full bg-gray-50 text-gray-900 ${shaftSource.required_options?.includes('playing_length') && !configuration.playing_length ? 'border-[#D4A55A]' : 'border-[#F3F4F6]'}`} data-testid="playing-length-select">
                        <SelectValue placeholder="Choose length" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg">
                        {shaftSource.playing_length_options.map(option => (
                          <SelectItem key={option.id} value={option.id} className="text-gray-900 hover:bg-gray-100">
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Advanced options (Tipping, Pured, Grip, Extra Wraps, Logo Position).
                    Grip/Wraps/Logo are product-level fields that must always be reachable
                    regardless of whether the shaft has Tipping/Pured options - previously
                    this whole block (including Grip) was gated behind a Tipping/Pured/Wraps/Logo
                    OR-check, which permanently hid the Grip picker (and made a required "Grip"
                    selection impossible) on any product with none of those four fields set. */}
                <div className="space-y-4" data-testid="advanced-options-body">

                {/* Tipping */}
                {(!needsShaftPicker || selectedShaftProduct) && shaftSource?.tipping_options?.length > 0 && (
                  <div>
                    <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-2 block">
                      Tipping
                      {shaftSource.required_options?.includes('tipping') && <span className="text-[#D4A55A] ml-1">*</span>}
                    </Label>
                    <Select value={configuration.tipping} onValueChange={(value) => setConfiguration(prev => ({ ...prev, tipping: value }))}>
                      <SelectTrigger className={`w-full bg-gray-50 text-gray-900 ${shaftSource.required_options?.includes('tipping') && !configuration.tipping ? 'border-[#D4A55A]' : 'border-[#F3F4F6]'}`} data-testid="tipping-select">
                        <SelectValue placeholder="Choose tipping" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg">
                        {shaftSource.tipping_options.map(option => (
                          <SelectItem key={option.id} value={option.id} className="text-gray-900 hover:bg-gray-100">
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Pured */}
                {(!needsShaftPicker || selectedShaftProduct) && shaftSource?.pured_options?.length > 0 && (
                  <div>
                    <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-2 block">
                      Pured
                      {shaftSource.required_options?.includes('pured') && <span className="text-[#D4A55A] ml-1">*</span>}
                    </Label>
                    <Select value={configuration.pured} onValueChange={(value) => setConfiguration(prev => ({ ...prev, pured: value }))}>
                      <SelectTrigger className={`w-full bg-gray-50 text-gray-900 ${shaftSource.required_options?.includes('pured') && !configuration.pured ? 'border-[#D4A55A]' : 'border-[#F3F4F6]'}`} data-testid="pured-select">
                        <SelectValue placeholder="Choose pured option" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg">
                        {shaftSource.pured_options.map(option => (
                          <SelectItem key={option.id} value={option.id} className="text-gray-900 hover:bg-gray-100">
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Grip */}
                {product.grip_options?.length > 0 && (
                  <GripPicker
                    options={product.grip_options}
                    value={configuration.grip}
                    onChange={(id) => setConfiguration(prev => ({ ...prev, grip: id }))}
                    required={product.required_options?.includes('grip')}
                    testId="grip-picker"
                  />
                )}
                
                {/* Grip Wraps */}
                {product.grip_wraps_options?.length > 0 && (
                  <div>
                    <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-2 block">
                      Extra Wraps
                      {product.required_options?.includes('grip_wraps') && <span className="text-[#D4A55A] ml-1">*</span>}
                    </Label>
                    <Select value={configuration.grip_wraps} onValueChange={(value) => setConfiguration(prev => ({ ...prev, grip_wraps: value }))}>
                      <SelectTrigger className={`w-full bg-gray-50 text-gray-900 ${product.required_options?.includes('grip_wraps') && !configuration.grip_wraps ? 'border-[#D4A55A]' : 'border-[#F3F4F6]'}`} data-testid="grip-wraps-select">
                        <SelectValue placeholder="Choose wraps" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg">
                        {product.grip_wraps_options.map(option => (
                          <SelectItem key={option.id} value={option.id} className="text-gray-900 hover:bg-gray-100">
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Grip Logo */}
                {product.grip_logo_options?.length > 0 && (
                  <div>
                    <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-2 block">
                      Logo Position
                      {product.required_options?.includes('grip_logo') && <span className="text-[#D4A55A] ml-1">*</span>}
                    </Label>
                    <Select value={configuration.grip_logo} onValueChange={(value) => setConfiguration(prev => ({ ...prev, grip_logo: value }))}>
                      <SelectTrigger className={`w-full bg-gray-50 text-gray-900 ${product.required_options?.includes('grip_logo') && !configuration.grip_logo ? 'border-[#D4A55A]' : 'border-[#F3F4F6]'}`} data-testid="grip-logo-select">
                        <SelectValue placeholder="Choose logo position" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg">
                        {product.grip_logo_options.map(option => (
                          <SelectItem key={option.id} value={option.id} className="text-gray-900 hover:bg-gray-100">
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                  </div>
                
                {/* Additional Notes */}
                <div>
                  <Label className="text-xs font-bold text-[#334155] uppercase tracking-widest mb-2 block">Additional Notes</Label>
                  <Textarea 
                    value={configuration.notes}
                    onChange={(e) => setConfiguration(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any special requests or instructions..."
                    className="w-full bg-gray-50 border-[#F3F4F6] text-gray-900 min-h-[100px]"
                    data-testid="notes-textarea"
                  />
                </div>
              </div>
            )}
            
            {/* Add to Cart */}
            <div className="mt-12 pt-8 border-t border-[#F3F4F6]">
              {/* Required fields error message - only shows after clicking Add to Cart in custom mode */}
              {purchaseMode === 'custom' && showRequiredError && missingFields.length > 0 && (
                <div className="mb-4 p-4 bg-[#FEF3C7] border border-[#F59E0B] rounded-lg" data-testid="required-fields-notice">
                  <div className="flex items-start space-x-3">
                    <span className="text-[#D97706] text-lg">⚠️</span>
                    <div>
                      <p className="text-[#92400E] font-semibold text-sm">Required Fields Missing</p>
                      <p className="text-[#A16207] text-sm mt-1">
                        Please select: <span className="font-semibold">{missingFields.join(', ')}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <button
                onClick={handleAddToCart}
                disabled={adding || product.stock === 0 || product.is_call_for_pricing || (purchaseMode === 'ready' && (!selectedOffShelf || (product.grip_options?.length > 0 && !readyToShipGrip)))}
                className="w-full h-14 px-8 bg-[#334155] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#475569] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3 lg:hidden"
                data-testid="add-to-cart-button"
              >
                {adding ? (
                  <>
                    <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-black border-r-transparent"></div>
                    <span>Adding...</span>
                  </>
                ) : (
                  <>
                    <ShoppingCart className="h-5 w-5" />
                    <span>{product.is_call_for_pricing ? 'Call for Pricing' : product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}</span>
                  </>
                )}
              </button>
            </div>
        </div>

        {/* Right: Sticky Build Summary */}
        <aside className="hidden lg:block lg:col-span-3 lg:sticky lg:top-20 lg:self-start">
          <BuildSummaryCard
            product={product}
            lineItems={lineItems}
            total={liveTotal}
            onAdd={handleAddToCart}
            adding={adding}
            stock={product.stock}
            baseLabel={isIron ? `Iron Heads (${ironClubs.length} ${ironClubs.length === 1 ? 'club' : 'clubs'}: ${ironClubs.join(', ') || '—'})` : 'Base'}
            basePrice={adjustedBase}
          />
        </aside>
      </div>

      {/* Specs + Reviews */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
        <SpecsTable specs={specs} />
        <ReviewsBlock productName={product.name} />
      </div>

      <Footer />
    </div>
  );
}
